import React, { useState } from 'react'
import { C, Spinner, Empty, Mono } from '../components/ui'
import { useFetch } from '../hooks/useFetch'
import { getUpcomingMatches, todayStr } from '../api/client'
import GameDetailDrawer from '../components/GameDetailDrawer'

function formatET(iso) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleTimeString('en-US', {
      timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit',
    })
  } catch { return iso }
}

function formatDay(iso) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      timeZone: 'America/New_York', weekday: 'short',
    })
  } catch { return '' }
}

function shiftDate(yyyymmdd, days) {
  const [y, m, d] = yyyymmdd.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + days)
  return dt.toISOString().slice(0, 10)
}

function prettyDate(yyyymmdd, today) {
  if (yyyymmdd === today) return 'Today'
  if (yyyymmdd === shiftDate(today, 1)) return 'Tomorrow'
  if (yyyymmdd === shiftDate(today, -1)) return 'Yesterday'
  const [y, m, d] = yyyymmdd.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
  })
}

// ── Color helpers ─────────────────────────────────────────────────────────────

function gradeColor(g) {
  if (g >= 65) return C.green
  if (g >= 50) return C.yellow
  return C.red
}

const LINEUP_COLOR = {
  Confirmed: C.green,
  Projected: C.yellow,
  TBD:       C.dim,
  Missing:   C.red,
}

const RISK_COLOR = {
  'Elite':      C.red,
  'Solid':      '#64b5f6',
  'Vulnerable': C.yellow,
  'High Risk':  C.green,
}

function pfIndicator(pf) {
  if (!pf) return { arrow: '—', color: C.dim }
  if (pf > 1.05) return { arrow: '↑', color: C.green }
  if (pf < 0.95) return { arrow: '↓', color: C.red }
  return { arrow: '→', color: C.dim }
}

const WIND_SHORT = {
  out_to_cf:  '↑Out',
  in_from_cf: '↓In',
  crossing:   '→X',
}

function weatherSummary(w) {
  if (!w || w.is_dome) return null
  const parts = []
  if (w.temp_f != null) parts.push(`${w.temp_f}°F`)
  if (w.wind_mph != null && w.wind_mph > 0) {
    const dir = WIND_SHORT[w.wind_relative] || ''
    parts.push(`${w.wind_mph}mph${dir ? ' ' + dir : ''}`)
  }
  return parts.length ? parts.join(' · ') : null
}

const LIVE_STATUSES = new Set(['In Progress', 'Warmup', 'Pre-Game', 'Manager Challenge', 'Delayed'])
const FINAL_STATUSES = new Set(['Final', 'Game Over'])

// ── Sub-components ────────────────────────────────────────────────────────────

function GradeBadge({ grade }) {
  const color = gradeColor(grade)
  return (
    <div style={{
      background: color + '22', border: `1px solid ${color}55`,
      borderRadius: 8, padding: '6px 10px', textAlign: 'center', minWidth: 52,
    }}>
      <div style={{
        fontFamily: "'Rajdhani', sans-serif", fontWeight: 800,
        fontSize: 22, color, lineHeight: 1,
      }}>{grade}</div>
      <div style={{
        fontFamily: "'Rajdhani', sans-serif", fontSize: 8,
        color, letterSpacing: '.08em', marginTop: 1,
      }}>GRADE</div>
    </div>
  )
}

function StatusPill({ label, color }) {
  return (
    <span style={{
      fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 9,
      letterSpacing: '.07em', textTransform: 'uppercase',
      color, padding: '2px 7px', borderRadius: 4,
      border: `1px solid ${color}40`, background: `${color}18`,
    }}>{label}</span>
  )
}

function MarketPill({ label }) {
  return (
    <span style={{
      fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 10,
      color: C.blue, border: `1px solid ${C.blueBdr}`,
      borderRadius: 4, padding: '2px 7px', background: C.blueBg,
    }}>{label}</span>
  )
}

function WarningPill({ label }) {
  return (
    <span style={{
      fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 9,
      color: C.yellow, border: `1px solid ${C.yellow}40`,
      borderRadius: 4, padding: '2px 7px', background: `${C.yellow}12`,
    }}>⚠ {label}</span>
  )
}

function Divider() {
  return <div style={{ borderTop: `1px solid ${C.dim2}`, margin: '8px 0' }} />
}

function MatchCard({ game, onClick }) {
  const { intel } = game
  const pf = intel?.park_factor
  const pfInd = pfIndicator(pf)
  const wx = weatherSummary(game.weather)
  const isLive  = LIVE_STATUSES.has(game.status)
  const isFinal = FINAL_STATUSES.has(game.status)
  const hasScore = game.home_score != null && game.away_score != null

  return (
    <div onClick={onClick} style={{
      background: C.bk3, border: `1px solid ${C.border}`,
      borderRadius: 10, padding: '12px 14px', margin: '8px 14px',
      cursor: 'pointer',
    }}>
      {/* ── Header: teams + grade ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            fontFamily: "'Rajdhani', sans-serif", fontWeight: 800,
            fontSize: 20, color: C.text, letterSpacing: '.02em',
          }}>
            {game.away.abbreviation || game.away.name}
            <span style={{ color: C.dim, fontWeight: 400, fontSize: 16, margin: '0 6px' }}>@</span>
            {game.home.abbreviation || game.home.name}
          </div>
          <div style={{ fontSize: 10, color: C.dim, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {isLive ? (
              <span style={{ color: C.red, fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", fontSize: 11 }}>● LIVE</span>
            ) : isFinal ? (
              <span style={{ color: C.dim }}>Final</span>
            ) : (
              <span>{formatDay(game.commence_time)} · {formatET(game.commence_time)} ET</span>
            )}
            {hasScore && (isLive || isFinal) && (
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, fontSize: 12, color: C.text }}>
                {game.away_score}–{game.home_score}
              </span>
            )}
            {wx && !isLive && !isFinal && (
              <span style={{ color: C.dim }}>· 🌡 {wx}</span>
            )}
          </div>
          {game.venue && !isLive && !isFinal && (
            <div style={{ fontSize: 10, color: C.dim, marginTop: 1 }}>🏟 {game.venue}</div>
          )}
        </div>
        {intel && <GradeBadge grade={intel.grade} />}
      </div>

      {/* ── Status + edges + implied runs ── */}
      {intel ? (
        <>
          <Divider />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            <StatusPill
              label={intel.lineup_status}
              color={LINEUP_COLOR[intel.lineup_status] || C.dim}
            />
            {intel.edge_count > 0 ? (
              <Mono size={10} color={C.text}>⚡ {intel.edge_count} edge{intel.edge_count !== 1 ? 's' : ''}</Mono>
            ) : (
              <Mono size={10} color={C.dim}>No edges above threshold</Mono>
            )}
            {intel.implied_runs != null && (
              <Mono size={10} color={C.dim}>≈ {intel.implied_runs} runs</Mono>
            )}
          </div>
        </>
      ) : (
        <>
          <Divider />
          <Mono size={10} color={C.dim}>No projections available</Mono>
        </>
      )}

      {/* ── Park factor + pitcher risk ── */}
      {intel && (pf != null || intel.pitcher_risk) && (
        <div style={{ display: 'flex', gap: 16, marginTop: 8, alignItems: 'center' }}>
          {pf != null && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Mono size={9} color={C.dim}>PARK</Mono>
              <Mono size={11} color={pfInd.color}>{pf.toFixed(2)} {pfInd.arrow}</Mono>
            </div>
          )}
          {intel.pitcher_risk && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Mono size={9} color={C.dim}>STARTER</Mono>
              <Mono size={11} color={RISK_COLOR[intel.pitcher_risk] || C.dim}>{intel.pitcher_risk}</Mono>
            </div>
          )}
        </div>
      )}

      {/* ── Best markets ── */}
      {intel?.best_markets?.length > 0 && (
        <>
          <Divider />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <Mono size={9} color={C.dim} style={{ marginRight: 2 }}>BEST</Mono>
            {intel.best_markets.map(m => <MarketPill key={m} label={m} />)}
          </div>
        </>
      )}

      {/* ── Top plays ── */}
      {intel?.top_plays?.length > 0 && (
        <>
          <Divider />
          <Mono size={9} color={C.dim} bold>TOP PLAYS</Mono>
          {intel.top_plays.map((p, i) => (
            <div key={i} style={{
              display: 'grid', gridTemplateColumns: '1fr 36px 44px',
              gap: 6, padding: '3px 0', alignItems: 'center',
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.name}
              </div>
              <Mono size={9} color={C.dim}>{p.market}</Mono>
              <Mono size={11} color={C.green} style={{ textAlign: 'right' }}>+{p.edge}%</Mono>
            </div>
          ))}
        </>
      )}

      {/* ── Why ── */}
      {intel?.why && (
        <div style={{
          marginTop: 8, fontSize: 10, color: C.dim,
          fontStyle: 'italic', lineHeight: 1.5,
        }}>
          {intel.why}
        </div>
      )}

      {/* ── Warnings ── */}
      {intel?.warnings?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
          {intel.warnings.map(w => <WarningPill key={w} label={w} />)}
        </div>
      )}
    </div>
  )
}

function ArrowBtn({ children, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: disabled ? 'none' : C.blueBg,
      border: `1px solid ${disabled ? C.border : C.blueBdr}`,
      borderRadius: 6, padding: '4px 10px',
      color: disabled ? C.dim : C.blue,
      fontSize: 14, lineHeight: 1, cursor: disabled ? 'default' : 'pointer',
      fontFamily: "'Rajdhani', sans-serif", fontWeight: 700,
    }}>{children}</button>
  )
}

const GRADE_FILTERS = [
  { label: 'All', min: 0 },
  { label: '60+', min: 60 },
  { label: '70+', min: 70 },
]

export default function UpcomingMatchesTab() {
  const today = todayStr()
  const [selectedDate, setSelectedDate] = useState(today)
  const [selectedGame, setSelectedGame] = useState(null)
  const [gradeFilter, setGradeFilter] = useState(0)

  const { data, loading, error } = useFetch(
    () => getUpcomingMatches(selectedDate),
    [selectedDate]
  )

  const allGames = data?.games || []
  const games = gradeFilter > 0
    ? allGames.filter(g => (g.intel?.grade ?? 0) >= gradeFilter)
    : allGames

  return (
    <div style={{ paddingBottom: 84 }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: `linear-gradient(180deg, ${C.bk} 90%, transparent)`,
        padding: '16px 16px 12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: C.blue, fontSize: 18 }}>◆</span>
            <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 20 }}>
              Games
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <ArrowBtn onClick={() => setSelectedDate(d => shiftDate(d, -1))}>‹</ArrowBtn>
            {selectedDate !== today && (
              <button onClick={() => setSelectedDate(today)} style={{
                background: 'none', border: `1px solid ${C.border}`,
                borderRadius: 6, padding: '4px 8px', color: C.dim,
                fontSize: 9, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700,
                letterSpacing: '.08em', cursor: 'pointer', textTransform: 'uppercase',
              }}>Today</button>
            )}
            <ArrowBtn onClick={() => setSelectedDate(d => shiftDate(d, 1))}>›</ArrowBtn>
          </div>
        </div>
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 11, color: C.dim }}>
            <span style={{ color: C.text, fontWeight: 700 }}>{prettyDate(selectedDate, today)}</span>
            {' · '}{games.length}{gradeFilter > 0 ? `/${allGames.length}` : ''} {allGames.length === 1 ? 'game' : 'games'}
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {GRADE_FILTERS.map(f => {
              const active = gradeFilter === f.min
              return (
                <button key={f.min} onClick={() => setGradeFilter(f.min)} style={{
                  fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 10,
                  letterSpacing: '.06em', padding: '3px 8px', borderRadius: 5, cursor: 'pointer',
                  border: `1px solid ${active ? C.blue : C.border}`,
                  background: active ? C.blueBg : 'none',
                  color: active ? C.blue : C.dim,
                }}>{f.label}</button>
              )
            })}
          </div>
        </div>
      </div>

      {loading && <Spinner />}
      {error && <Empty message="Could not load games" />}
      {!loading && !error && allGames.length === 0 && (
        <Empty message={data?.error ? `No games: ${data.error}` : 'No games scheduled'} />
      )}
      {!loading && !error && allGames.length > 0 && games.length === 0 && (
        <Empty message={`No games with grade ${gradeFilter}+`} />
      )}
      {!loading && !error && games.map(g => (
        <MatchCard key={g.id} game={g} onClick={() => setSelectedGame(g)} />
      ))}

      {selectedGame && (
        <GameDetailDrawer game={selectedGame} onClose={() => setSelectedGame(null)} />
      )}
    </div>
  )
}
